// Visitor browser detection for the /download page. Best-effort, synchronous,
// and SSR-safe: with no `navigator` we report `other`, which keeps the page on
// a neutral, all-equal layout instead of guessing.

export type DetectedBrowser =
  | 'firefox'
  | 'opera'
  | 'edge'
  | 'brave'
  | 'vivaldi'
  | 'chrome'
  | 'safari'
  | 'other';

// Which install tile to feature for a detected browser. Chromium-family
// browsers (Edge/Brave/Vivaldi) install the Chrome Web Store build, so they all
// map onto the `chrome` tile. Safari has no extension yet and unknown browsers
// stay neutral — both signalled by `null`.
export type InstallTarget = 'chrome' | 'firefox' | 'opera' | null;

interface UABrand {
  brand: string;
  version: string;
}

function brandList(): string[] {
  if (typeof navigator === 'undefined') return [];
  const data = (navigator as Navigator & { userAgentData?: { brands?: UABrand[] } }).userAgentData;
  return Array.isArray(data?.brands) ? data.brands.map((b) => b.brand) : [];
}

export function detectBrowser(): DetectedBrowser {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const brands = brandList();
  const hasBrand = (needle: string) =>
    brands.some((b) => b.toLowerCase().includes(needle.toLowerCase()));

  // Gecko is a distinct engine — safe to classify up front.
  if (/firefox|fxios/i.test(ua) && !/seamonkey/i.test(ua)) return 'firefox';

  // Chromium derivatives all carry the "Chrome" UA token, so the specific
  // brand/token checks must run before the generic Chrome fallthrough below.
  if (hasBrand('Microsoft Edge') || /\bEdg(?:e|A|iOS)?\//i.test(ua)) return 'edge';
  if (hasBrand('Opera') || /\bOPR\//i.test(ua) || /\bOpera\b/i.test(ua)) return 'opera';
  if (hasBrand('Vivaldi') || /\bVivaldi\//i.test(ua)) return 'vivaldi';
  // Brave masks itself in the UA string but injects `navigator.brave`.
  if (typeof (navigator as Navigator & { brave?: { isBrave?: unknown } }).brave?.isBrave === 'function') {
    return 'brave';
  }

  if (hasBrand('Google Chrome') || hasBrand('Chromium') || /chrome|chromium|crios/i.test(ua)) {
    return 'chrome';
  }

  // WebKit Safari is the last candidate: every Chromium UA also contains
  // "Safari", but those were already classified above.
  if (/safari/i.test(ua) && /applewebkit/i.test(ua)) return 'safari';

  return 'other';
}

export function installTargetFor(browser: DetectedBrowser): InstallTarget {
  switch (browser) {
    case 'firefox':
      return 'firefox';
    case 'opera':
      return 'opera';
    case 'chrome':
    case 'edge':
    case 'brave':
    case 'vivaldi':
      return 'chrome';
    case 'safari':
    case 'other':
    default:
      return null;
  }
}
